import React, { useCallback, useMemo } from "react";

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Plus } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import { toast } from "sonner";

import { Button } from "@/src/components/ui/button";

import {
  adminUserColumns,
  canEditAdminUser,
  getAdminUserHref,
} from "./tableParams/adminUserColumns";
import AdminUserAPI from "../../api/AdminUserAPI";
import AdminUser from "../../models/AdminUser";
import CappedPage from "../common/CappedPage";
import DataTable from "../dataTable/DataTable";
import { ADMIN_USERS_ROUTE } from "../routes/routes";

import type { JSX } from "react";

import type { AppState } from "../../reducers";

function AdminUsersList(): JSX.Element {
  const { t, i18n } = useTranslation("editor-users");
  const history = useHistory();
  const queryClient = useQueryClient();

  const loggedInUser = useSelector(
    (state: AppState) => state.fetchReducer.user.data,
  );

  const getAdminUserListQuery = AdminUserAPI.generateQuery({
    fn: AdminUserAPI.getAdminUserList,
    params: [],
  });

  const {
    data: adminUsersData,
    isLoading,
    isError,
    refetch,
  } = useQuery(getAdminUserListQuery);

  // `rolesString` est traduit à la construction : la langue relance le calcul.
  const userList = useMemo(
    (): AdminUser[] =>
      (adminUsersData?.users ?? []).map(
        (user) =>
          new AdminUser({
            ...user,
            userRoles: user.user_roles,
            avatarUrl: user.avatar_url,
            firstName: user.first_name,
            lastName: user.last_name,
          }),
      ),
    // eslint-disable-next-line react-hooks/exhaustive-deps -- AdminUser n'est reconstruit que si la liste ou la langue change
    [adminUsersData?.users, i18n.language],
  );

  const { mutate: deleteAdminUser } = useMutation({
    mutationFn: async (uuid: string) => AdminUserAPI.deleteAdminUser(uuid),
    onSuccess: () => {
      toast.success(t("userDeleted"));
      queryClient.invalidateQueries(getAdminUserListQuery.queryKey);
    },
    onError: () => {
      toast.error(t("error"));
    },
  });

  const getRowHref = useCallback(
    (user: AdminUser): string | undefined =>
      canEditAdminUser(user, loggedInUser.email)
        ? getAdminUserHref(user.uuid)
        : undefined,
    [loggedInUser.email],
  );

  const columnList = useMemo(
    () =>
      adminUserColumns({
        t,
        loggedInEmail: loggedInUser.email,
        onEdit: (user) => history.push(getAdminUserHref(user.uuid)),
        onDelete: (user) => deleteAdminUser(user.uuid),
      }),
    [t, loggedInUser.email, history, deleteAdminUser],
  );

  const handleAddEditor = (): void => {
    if (userList.length >= (adminUsersData?.users_limit ?? Infinity)) {
      toast.error(t("maxAdminUserReached"));
      return;
    }
    history.push(`${ADMIN_USERS_ROUTE}/new`);
  };

  const addButton = (
    <Button onClick={handleAddEditor} size="lg" type="button">
      <Plus />
      {t("addEditor")}
    </Button>
  );

  return (
    <CappedPage contentClassName="py-15">
      <DataTable
        columns={columnList}
        data={userList}
        defaultSorting={{ id: "lastName", desc: false }}
        getRowHref={getRowHref}
        headerActions={addButton}
        isError={isError}
        isLoading={isLoading}
        onRetry={refetch}
        title={t("manageUsers")}
        description={
          adminUsersData ? (
            <p className="text-muted-foreground text-sm">
              {t("usersQuota", {
                count: userList.length,
                limit: adminUsersData.users_limit,
              })}
            </p>
          ) : undefined
        }
      />
    </CappedPage>
  );
}

export default AdminUsersList;
